/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Injectable, Injector } from '@angular/core';
import {
  assign,
  chain,
  cloneDeep,
  countBy,
  difference,
  every,
  filter,
  find,
  first,
  forEach,
  get,
  groupBy,
  gt,
  includes,
  isArray,
  isEmpty,
  isEqual,
  isFunction,
  isNil,
  keys,
  map,
  merge,
  orderBy,
  pick,
  reduce,
  set,
  size,
  snakeCase,
  some,
  sortBy,
  toLower,
  values,
} from 'lodash';
import {
  ITreeConverterConfig,
  ITreeConverterReportCategoryAlias,
  ITreeData,
} from '@shared/types/common/tree.type';
import {
  flatTreeDataByHasChildren,
  generateId,
  generateUnitTextV2,
  getCreateAutoPopulateData,
  setGridKeyboardSupport,
  toDate,
  toMoment,
} from '@shared/utils';
import { BaseFormService } from '../base/base.form.service';
import {
  ENTITY_TYPES,
  GRID_LIST_VIEW_TYPE,
  GridRowData,
  IAPIErrorType,
  IBaseEntityResponse,
  IBaseEntityType,
  IEvent,
  IModalDialogResult,
  InitialFactorAndCountValue,
  IPageJumpRouteState,
  IQohInventoryItemVenue,
  IQuantityOnHandResponse,
  ISite,
  ISiteResponse,
  ITransferData,
  ITransferDestination,
  ITransferErrorMapper,
  ITransferGroupFormFieldPath,
  ITransferItem,
  ListColumn,
  Primitive,
  ToolbarButtonDefinition,
  TooltipIconConfig,
  TRANSFER_TYPE,
  ValueFormatterFn,
  XuxGridAction,
  XuxGridActionPayload,
  IApiListResponse,
  MEASURE_TRANSACTION,
  IMeasure,
  REPLENISH_NAME_OPTIONS,
} from '@shared/types';
import { TRANSFER_STATUS_VALUE } from '@shared/types/enums/transfer-status.enum';
import {
  getFormControl,
  getFormFieldValue,
  setFormFieldValue,
} from '@components/form/services/schema-form/schema-form-functions';
import {
  TRANSFER_FINAL_STATUS,
  TRANSFER_NOT_FINAL_STATUS,
  TRANSFER_UNSAVED_STATUS,
} from '@shared/services/transfer/transfer.definition';
import { COMMON_FORM_PATH } from '@shared/constants';
import { lazyInject } from '@containers/base/decorators';
import { NumberFormatService } from '../number-format';
import { BehaviorSubject, Observable, ObservableInput, Subject } from 'rxjs';
import { observableCombineLatest, observableOf, rx } from '@shared/rx';
import { UiActions } from '@store/actions';
import {
  createPendingQuantityMap,
  getCurrentItemQoh,
  getIsShowReceivedColStatus,
  getItemDmWarning,
  getItemThreshold,
  getItemThresholdConfig,
  getItemThresholdWarning,
  handleTransferUnitChange,
  isSiteTypeWarehouse,
  getParQty,
  qohResultOfDivFactorValue,
  updateInitialFactorAndCountValue,
  getDefaultOrSelectedUnitEntityId,
} from '@shared/services/transfer/transfer.util';
import {
  ColDef,
  ColumnApi,
  GridApi,
  ICellRendererParams,
  ValueFormatterParams,
} from 'ag-grid-community';
import { DecimalNumber } from '@shared/common';
import { Threshold } from '@geniuspos/xenial-data-contracts/types/dm-entities/inventory-item-venue';
import {
  TransferDestinationsContainerComponent,
  TransferEditContainerComponent,
} from '@containers/transfer/containers';
import { XuxEditGridComponent } from '@components/xux/xux-edit-grid/xux-edit-grid-widget.component';
import {
  getAllRowsAfterFilterAndSort,
  setColumnDef,
} from '@components/xvi-grid/custom-components/cell-renderer-components/renderer-helpers';
import { ENTITY_ID_PATH, MAX_URL_QUERY_NUMBER } from '@config/global-constants';
import { TransferService } from '@shared/services/transfer/transfer.service';
import { TransferPdfService } from '@shared/services/transfer/transfer.pdf.service';
import { SitesService } from '@shared/services/sites/sites.service';
import { getSingularEntityName } from '@shared/configs';
import { VISIBLE_TRANSFER_GRID_ROW_COUNT } from '@shared/services/transfer/transfer-constants';
import { transferModalDefinition } from '@shared/services/transfer/modal.definition';
import { PermissionService } from '../session/permission.service';
import { select } from '@angular-redux/store';
import { STATE_KEYS } from '../state-service';
import {
  IStandWorkSheetItem,
  IStandWorkSheetItemFullData,
} from '@app/shared/types/entities/stand-worksheet';
import { CalculationService } from '../stand-worksheet/calculation-services/calculation.types';
import { DmDataService } from '../dm-data/dm-data.service';
import { InventoryItemVenueService } from '../inventory-item-venue/inventory-item-venue.service';
import { TransferAutoRestockService } from '../transfer-auto-restock';
import { StandWorksheetCalcServiceProvider } from '../stand-worksheet/calculation-services/stand-worksheet-calc-service-provider';
import { TransferQtyService } from './transfer-qty.service';
import { ReplenishWorkerBuilder } from '../replenish-worker/replenish-worker.builder';
import {
  IReplenishDestination,
  REPLENISH_SCOPE_OPTIONS_VALUES,
} from '../replenish-worker';
import { REPLENISH_CONFIG_MAPPING } from '../transfer/transfer-replenish.constants';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { I18n } from '@app/i18n/strings-keys';
import { XVII18nService } from '@app/i18n/services';

@Injectable()
export class TransferBaseFormService extends BaseFormService {
  @select(STATE_KEYS.UI_EDITOR_OPTIONS_STATE)
  public routeState$: Observable<IPageJumpRouteState>;

  @lazyInject(NumberFormatService)
  public numberFormatService: NumberFormatService;
  @lazyInject(PermissionService) public permissionService: PermissionService;
  @lazyInject(TransferPdfService) public transferPdfService: TransferPdfService;
  @lazyInject(TransferService) public transferService: TransferService;
  @lazyInject(SitesService) public sitesService: SitesService;
  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(DmDataService) public dmDataService: DmDataService;
  @lazyInject(InventoryItemVenueService)
  public inventoryItemVenueService: InventoryItemVenueService;
  @lazyInject(TransferAutoRestockService)
  public transferAutoRestockService: TransferAutoRestockService;
  @lazyInject(StandWorksheetCalcServiceProvider)
  public standWorksheetCalcServiceProvider: StandWorksheetCalcServiceProvider;
  @lazyInject(TransferQtyService)
  public transferQtyService: TransferQtyService;
  @lazyInject(ReplenishWorkerBuilder)
  public replenishWorkerBuilder: ReplenishWorkerBuilder;
  @lazyInject(XVII18nService)
  public i18nService: XVII18nService;

  public hiddenModifiedInfo$: BehaviorSubject<boolean> = new BehaviorSubject(
    true,
  );
  public hiddenRemoveInfo$: BehaviorSubject<boolean> = new BehaviorSubject(
    true,
  );
  public hiddenThresholdInfo$: BehaviorSubject<boolean> = new BehaviorSubject(
    true,
  );
  public isFetchingEventData$: BehaviorSubject<boolean> = new BehaviorSubject(
    false,
  );
  public isInventoryItemVenuesGridBusy$: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  public isPendingQuantityFetching$: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  public isQohDataFetching$: BehaviorSubject<boolean> = new BehaviorSubject(
    false,
  );
  public modifiedInfoTitle$: Subject<string> = new Subject();
  public pendingQuantityData$: BehaviorSubject<IBaseEntityType> =
    new BehaviorSubject({});
  public qohData$: BehaviorSubject<IQohInventoryItemVenue[]> =
    new BehaviorSubject([]);
  public removeInfoTitle$: Subject<string> = new Subject();
  public replenishButtonDisabled$: BehaviorSubject<boolean> =
    new BehaviorSubject(true);
  public replenishMessageHidden$: BehaviorSubject<boolean> =
    new BehaviorSubject(true);
  public replenishMessageTitle$: BehaviorSubject<string> = new BehaviorSubject(
    '',
  );
  public thresholdInfoTitle$: Subject<string> = new Subject();
  public clearQtyRequestBtnDisable$: BehaviorSubject<boolean> =
    new BehaviorSubject(true);
  public clearQtyGroupBtnDisable$: BehaviorSubject<boolean> =
    new BehaviorSubject(true);

  public formFieldPath: ITransferGroupFormFieldPath = {
    count: 'count',
    initialCount: 'initial_count',
    inventoryItemVenues: 'inventory_items',
    measureEntityId: 'measure_entity_id',
    measureFactorValue: 'measure_factor_value',
    siteId: 'site_id',
    siteIdTo: 'site_id_to',
    status: 'status',
    transferType: 'transfer_type',
    virtualInventoryItemVenues: 'virtual.inventory_items',
    totalCost: 'total_cost',
  };
  public inventoryItemThresholdMapper: ITransferErrorMapper = null;
  public isPendingQuantityFetching: boolean = false;
  public isQohDataFetching: boolean = false;
  public isQohFromVisible: boolean = true;
  public listGridApi: GridApi;
  public pendingQuantityData: IBaseEntityType = {};
  public qohData: IQohInventoryItemVenue[] = [];
  public selectedEvent: IEvent;
  public voidAllowedMap: object = {};
  public xuxGrid: XuxEditGridComponent;
  // allInventoryItems for keep all items, use for print written sheet;
  public allInventoryItems: IQohInventoryItemVenue[] = [];
  public viewSettingToggleValue: boolean = false;
  public dateLockedErrorCode: string = 'date_locked';

  public get gridListView(): GRID_LIST_VIEW_TYPE {
    return this.stateService.getTransferItemListViewSetting();
  }

  public catchHttpError = (
    error: IAPIErrorType,
  ): ObservableInput<IBaseEntityType> => {
    const toast = {
      errors: {
        title: error.title,
        message: error?.meta?.message || '',
      },
    };
    this.uiActions.showErrorToasterMessage(toast);

    return observableOf({ items: [] });
  };

  public constructor(public injector: Injector) {
    super(injector);
  }

  /**
   * get transaction date range validator
   *
   * @param editor container editor object, use to get form value
   * @param deliveryFieldPath delivery date field path, can't large than this
   * @param minimum minimum date, can't lower than this, if not has value will use this.minimumDate
   * @returns transaction date range validator
   */
  public getTransactionDateRangeValidator(
    maximum?: string,
    minimum?: string,
    isPreEventTransfer?: boolean,
    cancelValidation?: boolean,
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      // Early exit: skip validation if not pre-event transfer or no value
      if (!isPreEventTransfer || !control.value || cancelValidation) {
        return null;
      }

      const minDate = minimum ? toDate(minimum) : null;
      const maxDate = maximum ? toDate(maximum) : null;

      // No constraints means no validation needed
      if (!minDate && !maxDate) {
        return null;
      }

      const valueMoment = toMoment(control.value);

      // Validate based on available constraints
      let isValid: boolean;
      if (minDate && maxDate) {
        isValid = valueMoment.isBetween(minDate, maxDate, undefined, '[]');
      } else if (minDate) {
        isValid = valueMoment.isSameOrAfter(minDate);
      } else {
        isValid = valueMoment.isSameOrBefore(maxDate);
      }

      return isValid
        ? null
        : {
            [this.dateLockedErrorCode]: this.i18nService.instant(
              I18n.ErrorMessages.DateIsLocked,
            ),
          };
    };
  }

  /**
   * return autoPopulateData when not change transfer type, or just return null when change transfer type
   *
   * @param options
   * @returns
   */
  public getAutoPopulateForNewData<T = IBaseEntityType>(options?: {
    /**
     * @default this.getFormFieldValue(this.formFieldPath.transferType)
     */
    targetTransferType?: TRANSFER_TYPE;
    targetFieldPath?: string | string[];
  }): T | null {
    // not create mode, just return null
    if (!this.isNewMode()) {
      return null;
    }

    const autoPopulateData = getCreateAutoPopulateData<T>(this.routeState$);

    const targetTransferType = get(
      options,
      'targetTransferType',
      this.getFormFieldValue(this.formFieldPath.transferType),
    );

    // not match target transfer type, just return null
    if (
      !isEmpty(targetTransferType) &&
      !isEqual(
        targetTransferType,
        get(autoPopulateData, this.formFieldPath.transferType),
      )
    ) {
      return null;
    }

    const targetFieldPath = get(options, 'targetFieldPath');

    if (isEmpty(targetFieldPath)) {
      return autoPopulateData;
    }

    return isArray(targetFieldPath)
      ? pick(autoPopulateData, targetFieldPath)
      : get(autoPopulateData, targetFieldPath);
  }

  public destroyFormDefinition(): void {
    this.voidAllowedMap = {};
    this.allInventoryItems = [];
    this.selectedEvent = null;
    this.viewSettingToggleValue = undefined;

    super.destroyFormDefinition();
  }

  public addCountAndErrorToItems(
    items: IQohInventoryItemVenue[],
  ): IQohInventoryItemVenue[] {
    const cloneItems = cloneDeep(items);
    const savedInventoryItemVenues = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualInventoryItemVenues,
    );
    const inventoryItemVenueQuantityMap = reduce(
      savedInventoryItemVenues,
      (itemMap, item) => ({
        ...itemMap,
        [item.item_entity_id]: {
          count: item.count,
          initial_count: item.initial_count,
        },
      }),
      {},
    );
    const mapperItem = this.getUpdatedItemsLevel();

    return map(cloneItems, (item) => {
      item._id = '';
      const count = get(
        inventoryItemVenueQuantityMap,
        `${item.entity_id}.count`,
      );
      const initialCount = get(
        inventoryItemVenueQuantityMap,
        `${item.entity_id}.initial_count`,
      );
      const errors: string[] = [];

      const isDmWarningOn = mapperItem[item.entity_id]
        ? mapperItem[item.entity_id].error
        : false;
      if (isDmWarningOn) errors.push('dmWarning');

      const itemThreshold: Threshold =
        getItemThreshold(
          item,
          this.isOutsideOfEventTransfer(),
          this.selectedEvent,
        ) || null;
      const iconsInfo = {
        ...getItemThresholdConfig(itemThreshold, item.origin_measures),
        dmWarning: isDmWarningOn,
      };

      const qohTo = this.getQohAndPendingQuantity(
        item,
        this.formFieldPath.siteIdTo,
      );
      const qohFrom = this.getQohAndPendingQuantity(
        item,
        this.formFieldPath.siteId,
      );
      const currentRow = {
        count,
        iconsInfo,
        qohTo,
        qohFrom,
        initial_count: initialCount,
        _id: mapperItem[item.entity_id] ? mapperItem[item.entity_id]._id : '',
      };
      const thresholdWarning = getItemThresholdWarning({
        cellValue: iconsInfo,
        rowData: { ...item, ...currentRow },
        numberFormatter: this.numberFormatService.getNumberFormatter(false),
        i18nService: this.i18nService,
      });
      if (!isNil(thresholdWarning)) {
        errors.push('quantityRequestThresholdWarning');
        this.inventoryItemThresholdMapper[item.entity_id].error = true;
      }

      return Object.assign({}, item, {
        ...currentRow,
        error: errors,
      });
    });
  }

  public getStandWorksheetItemsMap(
    standWorksheetItems: IStandWorkSheetItem[],
    swCalculationService: CalculationService,
  ): IStandWorkSheetItem {
    return reduce(
      standWorksheetItems,
      (resultMap, item) => {
        const totalAvailable =
          swCalculationService.calculateTotalAvailableColumn(
            item as IStandWorkSheetItemFullData,
          );

        return {
          ...resultMap,
          [item.inventory_item_entity_id]: {
            expected_qty: swCalculationService.calculateExpectedQuantityColumn(
              item as IStandWorkSheetItemFullData,
              totalAvailable,
            ),
            end: get(item, 'end', 0),
          },
        };
      },
      {} as IStandWorkSheetItem,
    );
  }

  public handleNoAvailableReplenishment(): void {
    this.replenishMessageTitle$.next(
      this.i18nService.translate(
        I18n.InfoMessages.NoAnyConfiguredTypeForItems,
        {
          type: this.i18nService.translate(REPLENISH_NAME_OPTIONS.PAR),
        },
      ),
    );
    this.replenishMessageHidden$.next(false);
  }

  public canBulkVoid(
    transfers: ITransferData[] = this.getFormVirtualTransferItems(),
    isAllCanVoid = false,
  ): boolean {
    return (
      !isEmpty(transfers) &&
      this.transferService.hasManagePermission() &&
      this.transferService.hasVoidPermission() &&
      !this.isPreEventTypeTransfer() &&
      (isAllCanVoid
        ? every(transfers, (transfer) =>
            isEqual(TRANSFER_STATUS_VALUE.Completed, transfer.status),
          )
        : some(transfers, (transfer) =>
            isEqual(TRANSFER_STATUS_VALUE.Completed, transfer.status),
          ))
    );
  }

  public canVoid(transfer: ITransferData, isSkipVoidChange = true): boolean {
    return (
      !isNil(transfer) &&
      this.transferService.hasManagePermission() &&
      this.transferService.hasVoidPermission() &&
      !this.isPreEventTypeTransfer() &&
      isEqual(TRANSFER_STATUS_VALUE.Completed, transfer.status) &&
      (isSkipVoidChange ? true : get(this.voidAllowedMap, transfer._id, false))
    );
  }

  /**
   * @deprecated should remove this and use convertInventoryItemVenuesToTree in the transfer items utils instead
   */
  public convertInventoryItemVenuesToTree(
    items: IQohInventoryItemVenue[],
  ): ITreeData[] {
    return this.getSortedTreeDataByConfig(items, {
      path: COMMON_FORM_PATH.reportingCategoryMajorName,
      groupName: 'name',
      additionalTreeFn: (name): IBaseEntityType => {
        return {
          isMajorCategoryRow: true,
          reporting_category_major: {
            name,
          },
        };
      },
      childrenConfig: {
        path: COMMON_FORM_PATH.reportingCategoryMinorName,
        additionalTreeFn: (name): IBaseEntityType => {
          return {
            isMinorCategoryRow: true,
            reporting_category_minor: {
              name,
            },
          };
        },
        groupName: 'name',
        childrenConfig: {
          path: 'name',
        },
      },
    });
  }

  public async generateVoidAllowedMap(
    transferItems: ITransferData[],
  ): Promise<void> {
    const completedTransferIds = map(
      filter(transferItems, { status: TRANSFER_STATUS_VALUE.Completed }),
      '_id',
    );
    const newVoidAllowedIds = difference(
      completedTransferIds,
      keys(this.voidAllowedMap),
    );

    if (isEmpty(newVoidAllowedIds)) {
      return;
    }

    const newVoidAllowedList = await this.transferService
      .getVoidAllowedList(newVoidAllowedIds)
      .toPromise();
    const newVoidAllowedMap = reduce(
      newVoidAllowedList,
      (pre, curr) => {
        return {
          ...pre,
          [curr._id]: curr.allowed,
        };
      },
      {},
    );

    this.voidAllowedMap = {
      ...this.voidAllowedMap,
      ...newVoidAllowedMap,
    };
  }

  public getClickReplenishToPARHidden(): boolean {
    const status: TRANSFER_STATUS_VALUE = this.getFormStatus();

    return includes(TRANSFER_FINAL_STATUS, status);
  }

  public getViewSettingButtonHidden(): boolean {
    const status: TRANSFER_STATUS_VALUE = this.getFormStatus();

    return includes(TRANSFER_FINAL_STATUS, status);
  }

  public defineListColumnDisplay(action: XuxGridAction): void {
    const columnApi: ColumnApi =
      action.payload?.gridApi['columnController'].columnApi;
    const gridApi: GridApi = action.payload?.gridApi;

    if (this.getIsShownQuantityReceivedCols()) {
      // shown initial request count col
      columnApi.setColumnsVisible([this.formFieldPath.count], true);

      setColumnDef(gridApi, this.formFieldPath.totalCost, {
        headerName: this.i18nService.instant(
          I18n.Forms.TransferForm.ReceivedCost,
        ),
      });
    }
  }

  public getFinalStatusItemsTreeData(items: ITransferItem[]): ITreeData {
    return this.getSortedTreeDataByConfig(items, {
      path: 'major_category_name',
      groupName: 'name',
      additionalTreeNode: { isMajorCategoryRow: true },
      childrenConfig: {
        path: 'minor_category_name',
        groupName: 'name',
        additionalTreeNode: { isMinorCategoryRow: true },
        childrenConfig: {
          path: 'name',
          majorAlias: {
            origin: 'major_category_name',
            backup: COMMON_FORM_PATH.reportingCategoryMajorName,
          },
          minorAlias: {
            origin: 'minor_category_name',
            backup: COMMON_FORM_PATH.reportingCategoryMinorName,
          },
          additionalFn: (item): ITransferItem => {
            return {
              name: item.item_name,
              gl_account: { name: item.gl_account_name },
              qoh_to: item.qoh_to,
              qoh_from: item.qoh_from,
              transfer_unit: generateUnitTextV2(
                {
                  description: item.measure_description,
                  factor_value: item.measure_factor_value,
                } as IMeasure,
                {
                  numberFormatService: this.numberFormatService,
                  measureTransactionType: MEASURE_TRANSACTION.Transfer,
                  notNeedAddSign: true,
                },
              ),
            };
          },
        },
      },
    });
  }

  public getFlattenInventoryItemVenueData<T>(data?: ITreeData[]): T[] {
    const inventoryItemData: ITreeData[] | T[] =
      data ??
      getFormFieldValue(this.form, this.formFieldPath.inventoryItemVenues);

    return flatTreeDataByHasChildren<T>(inventoryItemData);
  }

  public getFormStatus(): TRANSFER_STATUS_VALUE {
    return isNil(this.form)
      ? null
      : getFormFieldValue(this.form, this.formFieldPath.status);
  }

  public getFormTransferType(): TRANSFER_TYPE {
    return isNil(this.form)
      ? null
      : getFormFieldValue(this.form, this.formFieldPath.transferType);
  }

  public getFormVirtualTransferItems(): ITransferData[] {
    return isNil(this.form)
      ? null
      : getFormFieldValue(this.form, this.formFieldPath.virtualTransferItems);
  }

  public getGridReadyHandler(
    editor: TransferEditContainerComponent,
  ): (action: XuxGridAction, grid: XuxEditGridComponent) => void {
    return (action: XuxGridAction, grid: XuxEditGridComponent): void => {
      this.xuxGrid = grid;
      this.listGridApi = action.payload?.gridApi;

      if (!editor.canEdit || this.isFinalStatusFun()) {
        // disable arrow key
        setGridKeyboardSupport(grid.xuxGridComponent, false);
      }

      this.getQohFromVisible().then(() => {
        this.setQohFromVisible(action);
      });
      this.defineListColumnDisplay(action);
      action.payload?.gridApi.expandAll();
    };
  }

  public getIconsInfoRendererParams(): Record<
    string,
    string | boolean | object
  > {
    return {
      isBusy: observableCombineLatest([
        this.isQohDataFetching$,
        this.isPendingQuantityFetching$,
      ]).pipe(
        rx.map(
          ([isQohDataFetching, isPendingQuantityFetching]) =>
            isQohDataFetching || isPendingQuantityFetching,
        ),
      ),
      dataSource: observableCombineLatest([
        this.qohData$,
        this.pendingQuantityData$,
      ]),
      valueMapper: ({ rowData, cellValue, params }): TooltipIconConfig[] => {
        const result: TooltipIconConfig[] = [];
        const newErrorValue: string[] = [];

        if (
          !rowData.isLeaf ||
          isNil(this.form) ||
          this.isFinalStatusFun() ||
          !cellValue
        ) {
          params.node.setDataValue('error', newErrorValue);

          return result;
        }

        const { dmWarning } = cellValue;
        if (dmWarning) {
          result.push(getItemDmWarning());
          newErrorValue.push('dmWarning');
        }
        const thresholdWarning = getItemThresholdWarning({
          cellValue,
          rowData,
          numberFormatter: this.numberFormatService.getNumberFormatter(false),
          i18nService: this.i18nService,
        });
        if (!isNil(thresholdWarning)) {
          result.push(thresholdWarning);
          newErrorValue.push('quantityRequestThresholdWarning');
        }

        params.node.setDataValue('error', newErrorValue);
        if (
          this.inventoryItemThresholdMapper[rowData.entity_id].error !==
          newErrorValue.includes('quantityRequestThresholdWarning')
        ) {
          this.inventoryItemThresholdMapper[rowData.entity_id].error =
            newErrorValue.includes('quantityRequestThresholdWarning');
          this.setThresholdInfoMessage();
        }

        return result;
      },
    };
  }

  public getInlineChangeMapper(): (
    value: GridRowData,
    payload: XuxGridActionPayload,
  ) => GridRowData {
    return (value: GridRowData, payload: XuxGridActionPayload): GridRowData => {
      let newGridRowData: GridRowData = value;
      if (!this.isFinalStatusFun()) {
        switch (payload.field) {
          case this.formFieldPath.measureEntityId:
            const updatedRowData: GridRowData = handleTransferUnitChange(
              value,
              this.selectedEvent,
              this.isOutsideOfEventTransfer(),
            );

            newGridRowData = updatedRowData;
            break;
          case this.formFieldPath.count:
            const {
              count,
              receivedFactorValue,
            }: Pick<
              InitialFactorAndCountValue,
              'count' | 'receivedFactorValue'
            > = updateInitialFactorAndCountValue(value);

            newGridRowData = {
              ...value,
              // remove historicalInitialTotal value is from historical list new transfer use user type in
              historical_initial_total: undefined,
              initialFactorAndCountValue: merge(
                {},
                value.initialFactorAndCountValue,
                { count, receivedFactorValue },
              ),
            };
            break;
          case this.formFieldPath.initialCount:
            const {
              initial_count,
              factorValue,
            }: Pick<
              InitialFactorAndCountValue,
              'initial_count' | 'factorValue'
            > = updateInitialFactorAndCountValue(value);

            newGridRowData = {
              ...value,
              // remove historicalInitialTotal value is from historical list new transfer use user type in
              historical_initial_total: undefined,
              initialFactorAndCountValue: merge(
                {},
                value.initialFactorAndCountValue,
                { initial_count, factorValue },
              ),
            };
            break;
          default:
            newGridRowData = value;
            break;
        }
      }
      this.updateSavedInventoryItems(newGridRowData);

      return newGridRowData;
    };
  }

  public getIsDisableTopSection(): boolean {
    const status = this.getFormStatus();
    const notFinalStatusPendingTransfer =
      !this.isPreEventTypeTransfer() &&
      isEqual(TRANSFER_STATUS_VALUE.Pending, status);

    return (
      notFinalStatusPendingTransfer || includes(TRANSFER_FINAL_STATUS, status)
    );
  }

  public getIsShownQuantityReceivedCols(): boolean {
    const transferType: TRANSFER_TYPE = this.getFormTransferType();
    const status = this.getFormStatus();

    return getIsShowReceivedColStatus(transferType, status);
  }

  public getMeasureEntityIdCellRendererParams(
    params: ICellRendererParams,
    editor:
      | TransferEditContainerComponent
      | TransferDestinationsContainerComponent,
    visibleRowCount: number = VISIBLE_TRANSFER_GRID_ROW_COUNT,
  ): ListColumn['cellRendererParams'] {
    const measures = params.data.measures;
    if (isEmpty(measures)) {
      return null;
    }

    const rowCount = params.api.getDisplayedRowCount();
    const isLastRow = params.rowIndex === rowCount - 1;
    const isSecondToLastRow =
      params.rowIndex === rowCount - 2 && measures.length >= 2;
    const isThirdToLastRow =
      params.rowIndex === rowCount - 3 && measures.length >= 3;
    // We take into account the last 3 rows, since the max dropdown height is 3 items.
    // (visibleRowCount - 1) - to cover the case when 4 out of 5 rows are displayed on the grid and
    // the last item in the dropdown does not fit in the grid.
    const isPositionTop =
      (isLastRow && rowCount >= visibleRowCount - 1 && measures.length >= 2) ||
      ((isSecondToLastRow || isThirdToLastRow || isLastRow) &&
        rowCount >= visibleRowCount);

    return {
      isPositionTop,
      options: measures,
      disabled: !editor.canEdit,
      value: params.data[this.formFieldPath.measureEntityId],
    };
  }

  public getPendingQuantity(
    siteId: string,
    siteIdTo: string,
  ): Observable<IBaseEntityResponse<IBaseEntityType>> {
    this.isPendingQuantityFetching$.next(true);

    return this.transferService
      .fetchList(
        {
          filter: `status eq 'pending' and ((site_id eq in(${siteId},${siteIdTo})) or (site_id_to eq in(${siteId},${siteIdTo})))`,
          select: [
            'site_id',
            'site_id_to',
            'inventory_items.item_entity_id',
            'inventory_items.initial_count',
            'inventory_items.measure_factor_value',
          ],
        },
        {
          skipMappers: true,
        },
        true,
      )
      .pipe(
        rx.map((res) => {
          if (!isEqual(TRANSFER_STATUS_VALUE.Pending, this.document?.status)) {
            return res;
          }

          return {
            ...res,
            items: filter(
              res.items,
              (transfer) => transfer._id !== this.document._id,
            ),
          };
        }),
        rx.catchError(this.catchHttpError),
        rx.tap((res) => {
          const pendingQtyMap = createPendingQuantityMap(
            this,
            res,
            siteId,
            siteIdTo,
          );
          this.pendingQuantityData = pendingQtyMap;
          this.pendingQuantityData$.next(pendingQtyMap);
          this.isPendingQuantityFetching$.next(false);
          this.transferPdfService.setPendingQty(pendingQtyMap);
          this.isPendingQuantityFetching = false;
        }),
      );
  }

  /**
   * @deprecated should use getQohAndPendingQuantity in replenish utils instead
   */
  public getQohAndPendingQuantity(
    item: IQohInventoryItemVenue,
    path: string,
    { qohData, pendingQtyMap } = {
      qohData: this.qohData,
      pendingQtyMap: this.pendingQuantityData,
    },
    siteId = getFormFieldValue(this.form, path),
  ): string[] {
    const qohDataForCurrentRow = getCurrentItemQoh(qohData, siteId, item);
    const pendingQtyForCurrentRow = get(pendingQtyMap, item.entity_id);

    return [
      get(qohDataForCurrentRow, this.formFieldPath.count, 0)?.toString(),
      get(pendingQtyForCurrentRow, path, 0)?.toString(),
    ];
  }

  public getQohValueGetter(type: string): ValueFormatterFn {
    return (params: ValueFormatterParams): string => {
      if (!params.data.isLeaf) return null;
      // in final status, we can directly get qohValue and factor value for calculating.
      // there is distinct from other status.
      if (this.isFinalStatusFun()) {
        const qohValue = new DecimalNumber(params.data[snakeCase(type)]);
        const divisionResult: number = qohValue
          .div(get(params.data, 'measure_factor_value'))
          .valueOf();

        return this.numberFormatService.getNumberFormatter(false)({
          value: divisionResult,
        });
      }

      return qohResultOfDivFactorValue(params, type, this.numberFormatService);
    };
  }

  public getQuantityCellClassRules(
    canEditStatus: TRANSFER_STATUS_VALUE[],
  ): ColDef['cellClassRules'] {
    return {
      'ag-numeric-cell': (): boolean => true,
      'show-placeholder': ({ value }): boolean => {
        const isValidValue = !(value || value === 0);
        // shown placeholder when can edit it
        if (
          isEmpty(canEditStatus) ||
          (!isEmpty(canEditStatus) &&
            includes(canEditStatus, this.getFormStatus()))
        ) {
          return isValidValue;
        }

        return false;
      },
    };
  }

  public getQuantityOnHandData(
    inventoryItemVenues: IQohInventoryItemVenue[],
    siteId: string,
    siteIdTo: string,
  ): Observable<
    | IBaseEntityType
    | IQuantityOnHandResponse
    | IBaseEntityResponse<IBaseEntityType>
  > {
    if (isEmpty(inventoryItemVenues)) {
      return observableOf(null);
    }
    this.isQohDataFetching$.next(true);

    return this.transferService
      .getQuantityOnHandData(
        inventoryItemVenues,
        this.isQohFromVisible ? siteId : '',
        siteIdTo,
        ENTITY_ID_PATH,
      )
      .pipe(
        rx.catchError(this.catchHttpError),
        rx.tap((res) => {
          this.qohData = res.items;
          this.qohData$.next(res.items);
          this.isQohDataFetching$.next(false);
          this.transferPdfService.setQOHData(res.items);
          this.isQohDataFetching = false;
        }),
      );
  }

  /**
   * @deprecated should remove this and use getSortedTreeDataByConfig in the transfer items utils instead
   */
  public getSortedTreeDataByConfig(
    data: IQohInventoryItemVenue[] | ITransferItem[],
    config: ITreeConverterConfig,
  ): ITreeData[] {
    // The path used for sorting and grouping
    const path: string = config.path;
    // The groupName used for group naming
    const groupName: string = config.groupName;
    const majorAlias: ITreeConverterReportCategoryAlias =
      config.majorAlias || {};
    const minorAlias: ITreeConverterReportCategoryAlias =
      config.minorAlias || {};
    const newItems = map(data, (item) => {
      return assign(
        {},
        item,
        {
          internal_grid_row_id: `local${generateId()}`,
          isLeaf: true,
          ...config.additional,
        },
        majorAlias.backup
          ? { [majorAlias.backup]: item[majorAlias.origin] }
          : {},
        minorAlias.backup
          ? { [minorAlias.backup]: item[minorAlias.origin] }
          : {},
        config.additionalFn ? config.additionalFn(item) : {},
      );
    });
    const sortItems = orderBy(
      newItems,
      ['is_chargeable', (item): string => toLower(get(item, path, ''))],
      ['desc', 'asc'],
    );

    if (!groupName) return sortItems;

    return chain(sortItems)
      .groupBy(path)
      .toPairs()
      .map(([name, children]) => {
        const childrenConfig = config.childrenConfig;
        const newChildren = this.getSortedTreeDataByConfig(
          children,
          childrenConfig,
        );

        return {
          [groupName]: name,
          children: newChildren,
          error: [],
          ...config.additionalTreeNode,
          ...(config.additionalTreeFn ? config.additionalTreeFn(name) : {}),
          isLeaf: false,
        };
      })
      .sortBy((item): string => toLower(get(item, groupName, '')))
      .value();
  }

  public getUpdatedItemsLevel(): ITransferErrorMapper {
    const savedInventoryItemVenues = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualInventoryItemVenues,
    );

    return reduce(
      savedInventoryItemVenues,
      (itemMap, item) => {
        return {
          ...itemMap,
          [item.item_entity_id]: {
            error: item.error ?? false,
            _id: item._id,
          },
        };
      },
      {},
    );
  }

  public initItemListSubscription(): void {
    const subscription = observableCombineLatest([
      this.form
        .get(this.formFieldPath.inventoryItemVenues)
        .valueChanges.pipe(rx.startWith([])),
      this.isQohDataFetching$,
      this.isPendingQuantityFetching$,
      this.isFetchingEventData$,
    ])
      .pipe(rx.distinctUntilChanged(isEqual))
      .subscribe(
        ([
          items,
          isQohDataFetching,
          isPendingQuantityFetching,
          isExistEventEntityId,
        ]: [IQohInventoryItemVenue[], boolean, boolean, boolean]): void => {
          this.replenishMessageHidden$.next(true);
          this.replenishButtonDisabled$.next(
            (isEmpty(items) && isEmpty(this.allInventoryItems)) ||
              isQohDataFetching ||
              isPendingQuantityFetching ||
              isExistEventEntityId,
          );
          this.clearQtyRequestBtnDisable$.next(
            isEmpty(items) || !this.enableClearQtyRequestedBtn(),
          );
          this.clearQtyGroupBtnDisable$.next(
            isEmpty(items) || !this.enableClearQtyBtnGroup(),
          );
        },
      );
    this.subscriptions.push(subscription);
  }

  public isFinalStatusFun(): boolean {
    const statusValue: TRANSFER_STATUS_VALUE = this.getFormStatus();

    return includes(TRANSFER_FINAL_STATUS, statusValue);
  }

  public isPreEventTypeTransfer(type?: TRANSFER_TYPE): boolean {
    return this.isTransferByType(TRANSFER_TYPE.PRE_EVENT_TRANSFER, type);
  }

  public isDuringEventTransfer(type?: TRANSFER_TYPE): boolean {
    return this.isTransferByType(TRANSFER_TYPE.DURING_EVENT_TRANSFER, type);
  }

  public isOutsideOfEventTransfer(type?: TRANSFER_TYPE): boolean {
    return this.isTransferByType(TRANSFER_TYPE.OUTSIDE_OF_EVENT_TRANSFER, type);
  }

  public hideClearQtyRequestedBtn(): boolean {
    const isPreEventTypeTransfer = this.isPreEventTypeTransfer(
      getFormFieldValue(this.form, this.formFieldPath.transferType),
    );
    if (isPreEventTypeTransfer) {
      return includes([...TRANSFER_FINAL_STATUS], this.getFormStatus());
    } else {
      return !includes(
        [...TRANSFER_UNSAVED_STATUS, TRANSFER_STATUS_VALUE.Open, null],
        this.getFormStatus(),
      );
    }
  }

  public enableClearQtyRequestedBtn(): boolean {
    return (
      (this.isPreEventTypeTransfer(
        getFormFieldValue(this.form, this.formFieldPath.transferType),
      ) &&
        includes([...TRANSFER_NOT_FINAL_STATUS, null], this.getFormStatus())) ||
      (!this.isPreEventTypeTransfer(
        getFormFieldValue(this.form, this.formFieldPath.transferType),
      ) &&
        includes(
          [...TRANSFER_UNSAVED_STATUS, TRANSFER_STATUS_VALUE.Open],
          this.getFormStatus(),
        ))
    );
  }

  public hideClearQtyBtnGroup(): boolean {
    return (
      this.isPreEventTypeTransfer(
        getFormFieldValue(this.form, this.formFieldPath.transferType),
      ) ||
      (!this.isPreEventTypeTransfer(
        getFormFieldValue(this.form, this.formFieldPath.transferType),
      ) &&
        includes(
          [
            ...TRANSFER_UNSAVED_STATUS,
            ...TRANSFER_FINAL_STATUS,
            TRANSFER_STATUS_VALUE.Open,
            null,
          ],
          this.getFormStatus(),
        ))
    );
  }

  public enableClearQtyBtnGroup(): boolean {
    return (
      !this.isPreEventTypeTransfer(
        getFormFieldValue(this.form, this.formFieldPath.transferType),
      ) && isEqual(TRANSFER_STATUS_VALUE.Pending, this.getFormStatus())
    );
  }

  public isTransferByType(
    condition: TRANSFER_TYPE,
    typeValue?: TRANSFER_TYPE,
  ): boolean {
    const transferTypeValue: TRANSFER_TYPE = typeValue
      ? typeValue
      : getFormFieldValue(this.form, this.formFieldPath.transferType);

    return isEqual(condition, transferTypeValue);
  }

  /**
   * @deprecated should use isSkipReplenish in replenish utils instead
   */
  public isSkipReplenish(destination: ITransferDestination): boolean {
    return (
      isEqual(destination?.noAvailablePAR, true) ||
      isEqual(destination?.skipReplenish, true)
    );
  }

  public async onClickReplenishHandler(replenishBtn: ToolbarButtonDefinition) {
    let filteredItems = map(
      getAllRowsAfterFilterAndSort(this.listGridApi),
      'data',
    );
    if (!this.isListView()) {
      filteredItems = filter(filteredItems, {
        isLeaf: true,
      });
    }
    const haveSearch: boolean = this.listGridApi.isAnyFilterPresent(); // true have; false no

    const noFilter: boolean = !haveSearch && !this.viewSettingToggleValue;
    const filteredNone: boolean =
      (haveSearch || this.viewSettingToggleValue) && isEmpty(filteredItems);

    const disabledReplenishOption: boolean = noFilter || filteredNone;

    const replenishHandlerMapper =
      REPLENISH_CONFIG_MAPPING[replenishBtn.caption as string];

    const routeState = this.stateService.getByPath(
      STATE_KEYS.UI_EDITOR_OPTIONS_STATE,
    );

    await this.replenishWorkerBuilder
      .build({
        additionDataOpts: {
          preInventory: [
            () => ({
              event: this.selectedEvent,
            }),
          ],
          afterInventory: replenishHandlerMapper?.afterInventory,
        },
        on: {
          afterReplenish: (replenishRes) => {
            const { destinations, replenishParams } = replenishRes;
            const replenishItemsScope = get(
              replenishParams,
              'replenishItemsScope',
              REPLENISH_SCOPE_OPTIONS_VALUES.ALL_ITEMS,
            );

            const replenishedDestination =
              destinations as IReplenishDestination;

            if (replenishedDestination.noAvailablePAR) {
              this.handleNoAvailableReplenishment();

              return;
            }

            const pageOriginalItems =
              this.getFlattenInventoryItemVenueData<IQohInventoryItemVenue>();
            const replenishedItems: IQohInventoryItemVenue[] =
              replenishedDestination.inventory_items;
            const updateToFormItems: IQohInventoryItemVenue[] =
              this.prepareReplenishItems({
                replenishItemsScope,
                disabledReplenishOption,
                haveSearch,
                replenishedItems,
                pageOriginalItems,
              });

            this.updateInventoryItemVenues(
              updateToFormItems,
              !isEqual(pageOriginalItems, updateToFormItems),
              false,
              {
                forceNotFillHistorical: true,
              },
            );
            forEach(replenishedItems, (item) =>
              this.updateSavedInventoryItems(item),
            );
          },
        },
      })
      .replenish({
        destination: {
          disabledReplenishOption,
          fromSite:
            get(routeState, 'storeForm.site_id') ||
            this.getFormFieldValue(this.formFieldPath.siteId),
          toSite:
            get(routeState, 'storeForm.site_id_to') ||
            this.getFormFieldValue(this.formFieldPath.siteIdTo),
          all_inventory_items: this.allInventoryItems,
          filtered_inventory_items: filteredItems,
        },
        strategy: replenishHandlerMapper?.strategy,
        confirmModalOptions: {
          modalTitle: replenishHandlerMapper?.modalTitle,
        },
      });
  }

  public prepareReplenishItems(prepareOption: {
    replenishItemsScope: REPLENISH_SCOPE_OPTIONS_VALUES;
    disabledReplenishOption: boolean;
    haveSearch: boolean;
    replenishedItems: IQohInventoryItemVenue[];
    pageOriginalItems: IQohInventoryItemVenue[];
  }): IQohInventoryItemVenue[] {
    const {
      replenishItemsScope,
      disabledReplenishOption,
      haveSearch,
      replenishedItems,
      pageOriginalItems,
    } = prepareOption;
    let updateToFormItems: IQohInventoryItemVenue[] = [];
    const isSelectAll: boolean = isEqual(
      replenishItemsScope,
      REPLENISH_SCOPE_OPTIONS_VALUES.ALL_ITEMS,
    );
    if (
      disabledReplenishOption || // (no-filter && off) || (filter && no-data)
      (this.viewSettingToggleValue && !isSelectAll && !haveSearch) // on && no-filter && filter-items
    ) {
      updateToFormItems = replenishedItems;
    }

    if (
      this.viewSettingToggleValue &&
      isSelectAll // on && all-items
    ) {
      updateToFormItems = filter(
        replenishedItems,
        (item) => !isNil(item.initial_count),
      );
    }

    if (
      (this.viewSettingToggleValue && !isSelectAll && haveSearch) || // on && && filter-items && filter
      (!this.viewSettingToggleValue && haveSearch) // off && filter
    ) {
      updateToFormItems = map(pageOriginalItems, (item) => {
        const replenishItem = find(replenishedItems, {
          entity_id: item.entity_id,
        });

        return isNil(replenishItem) ? item : replenishItem;
      });
    }

    return updateToFormItems;
  }

  public processInventoryItems(res: IBaseEntityResponse): IBaseEntityResponse {
    forEach(res.items, (inventoryItem: IQohInventoryItemVenue) => {
      const virtualInventoryItems = getFormFieldValue(
        this.form,
        this.formFieldPath.virtualInventoryItemVenues,
      );
      const measure = find(
        virtualInventoryItems,
        (item) => item.item_entity_id === inventoryItem?.entity_id,
      );
      const measures = inventoryItem.measures || [];
      const transferMeasures = filter(
        measures,
        (item) => item.transactions.transfer,
      );
      // sort the transfer Unit by description
      const sortMeasures = sortBy(
        map(transferMeasures, (filterItem) => {
          return {
            ...filterItem,
            text: generateUnitTextV2(filterItem, {
              numberFormatService: this.numberFormatService,
              measureTransactionType: MEASURE_TRANSACTION.Transfer,
            }),
            value: filterItem.entity_id,
            description: filterItem.description,
          };
        }),
        (item) => toLower(item.description),
      );

      set(inventoryItem, 'origin_measures', measures);
      set(inventoryItem, 'measures', sortMeasures);
      set(inventoryItem, 'error', []);

      const itemThreshold: Threshold =
        getItemThreshold(
          inventoryItem,
          this.isOutsideOfEventTransfer(),
          this.selectedEvent,
        ) || null;
      const iconsInfo = getItemThresholdConfig(
        itemThreshold,
        inventoryItem.origin_measures,
      );
      set(inventoryItem, 'iconsInfo', iconsInfo);
      // if the document have the measureEntityId use it else use first value
      const measureEntityId = get(
        measure,
        this.formFieldPath.measureEntityId,
        null,
      );
      inventoryItem[this.formFieldPath.measureEntityId] =
        getDefaultOrSelectedUnitEntityId(measureEntityId, sortMeasures);
      inventoryItem[this.formFieldPath.initialCount] = undefined;
      const parQty = getParQty(
        inventoryItem,
        this.selectedEvent,
        this.isOutsideOfEventTransfer(),
      );
      set(inventoryItem, 'par_qty', parQty);
    });

    return res;
  }

  public processParQty(
    inventoryItems: IQohInventoryItemVenue[],
  ): IQohInventoryItemVenue[] {
    forEach(inventoryItems, (inventoryItem: IQohInventoryItemVenue) => {
      const parQty = getParQty(
        inventoryItem,
        this.selectedEvent,
        this.isOutsideOfEventTransfer(),
      );
      set(inventoryItem, 'par_qty', parQty);
    });

    return inventoryItems;
  }

  public setInfoMessage(): void {
    const updatedItems = this.document?.updated_items;
    const unavailableItems = this.document?.unavailable_items;

    const type: ENTITY_TYPES.TRANSFER = this.document?.type;
    if (!isEmpty(updatedItems)) {
      this.hiddenModifiedInfo$.next(false);
      setTimeout(() => {
        this.modifiedInfoTitle$.next(
          this.i18nService.translate(I18n.InfoMessages.ItemListModified, {
            count: values(updatedItems).length,
            entityType: this.i18nService.translate(getSingularEntityName(type)),
          }),
        );
      });
    }
    if (!isEmpty(unavailableItems)) {
      const noLongerAvailableItems: string[] = map(
        values(unavailableItems),
        (item) => {
          return `[${item.item_name}]`;
        },
      );
      this.hiddenRemoveInfo$.next(false);
      setTimeout(() => {
        this.removeInfoTitle$.next(
          `${this.i18nService.translate(
            I18n.InfoMessages.ItemsIsNoLongerAvailable,
            {
              entityType: this.i18nService.translate(
                getSingularEntityName(type),
              ),
            },
          )} ${noLongerAvailableItems.join(', ')}.`,
        );
      });
    }
  }

  public setThresholdInfoMessage(): void {
    const needThresholdWarningItems = filter(
      this.inventoryItemThresholdMapper,
      (item) => item.error,
    );
    this.hiddenThresholdInfo$.next(isEqual(size(needThresholdWarningItems), 0));
    if (values(needThresholdWarningItems).length > 0) {
      setTimeout(() => {
        this.thresholdInfoTitle$.next(
          this.i18nService.translate(
            I18n.InfoMessages.ThresholdWarningMessage,
            {
              count: values(needThresholdWarningItems).length,
            },
          ),
        );
      });
    }
  }

  /**
   * @deprecated should use takeIntersectionAndProcessThreshold in the replenish utils instead
   */
  public takeIntersectionAndProcessThreshold(
    res: IBaseEntityResponse,
    siteIdTo: string,
  ): IQohInventoryItemVenue[] {
    const itemCountDictionary = countBy(res.items, 'entity_id');

    return filter(
      res.items,
      (item: IQohInventoryItemVenue) =>
        itemCountDictionary[item.entity_id] > 1 &&
        isEqual(item.site_id, siteIdTo),
    ) as IQohInventoryItemVenue[];
  }

  public takeIntersectionAndProcessThresholdForMultiFrom(
    res: any,
    destination: any,
  ): IQohInventoryItemVenue[] {
    const groupAllInventoryItems = groupBy(res.items, 'siteType');
    const itemCountDictionary = groupBy(res.items, 'entity_id');

    const data = filter(res.items, (item: any) => {
      const hasFromSite = find(
        groupAllInventoryItems.from,
        (singleItem) =>
          isEqual(singleItem.entity_id, item.entity_id) &&
          isEqual(singleItem.site_id, destination.site_id),
      );
      const hasToSite = find(
        groupAllInventoryItems.to,
        (singleItem) =>
          isEqual(singleItem.entity_id, item.entity_id) &&
          isEqual(singleItem.site_id, destination.id),
      );

      return (
        hasFromSite &&
        hasToSite &&
        isEqual(item.site_id, destination.id) &&
        isEqual(item.siteType, 'to')
      );
    }) as IQohInventoryItemVenue[];

    return data;
  }

  public updateInventoryItemVenues(
    items: IQohInventoryItemVenue[],
    markField: boolean = false,
    // this params is not same at transfer group and transfer, pls be careful
    saveUpdatedItems: boolean = false,
    additionalOptions?: {
      forceNotFillHistorical?: boolean;
    },
  ): void {
    // historical quantities create transfer should fill the Quantity Requested
    const updatedParInventoryItems: IQohInventoryItemVenue[] = map(
      items,
      (item) => ({
        ...item,
        initialFactorAndCountValue: updateInitialFactorAndCountValue(
          item as Partial<IQohInventoryItemVenue>,
        ),
      }),
    );
    const treeData = this.convertInventoryItemVenuesToTree(
      updatedParInventoryItems,
    );
    this.updateItemsByCompanyPreferences(
      treeData,
      this.formFieldPath.inventoryItemVenues,
      markField,
    );
    getFormControl(
      this.form,
      this.formFieldPath.inventoryItemVenues,
    )?.updateValueAndValidity({
      onlySelf: true,
      emitEvent: true,
    });
    this.isInventoryItemVenuesGridBusy$.next(false);
    setTimeout(() => {
      this.listGridApi.expandAll();
    });
  }

  public getQohFromVisible(): Promise<ISiteResponse> {
    return this.sitesService
      .fetchList({
        filter: `_id eq objectid(${getFormFieldValue(this.form, this.formFieldPath.siteId)})`,
        select: ['type', 'business_type'],
      })
      .pipe(
        rx.tap((response) => {
          const isWarehouseSite = isSiteTypeWarehouse(
            first(response.items as ISite[]),
          );

          if (isWarehouseSite) {
            this.isQohFromVisible =
              this.stateService.getTransferDisplayQohFrom();
          } else {
            this.isQohFromVisible = true;
          }
        }),
        rx.first(),
      )
      .toPromise();
  }

  public isListView(): boolean {
    return isEqual(
      this.gridListView,
      GRID_LIST_VIEW_TYPE.ALPHABETICAL_LIST_VIEW,
    );
  }

  public filterBlankRows(
    items: IQohInventoryItemVenue[],
  ): IQohInventoryItemVenue[] {
    const status = getFormFieldValue(this.form, this.formFieldPath.status);
    if (
      isEqual(status, TRANSFER_STATUS_VALUE.Pending) &&
      !this.isPreEventTypeTransfer()
    ) {
      return filter(
        items,
        (item) =>
          !isNil(item[this.formFieldPath.count]) ||
          !isNil(item[this.formFieldPath.initialCount]),
      );
    }

    return filter(
      items,
      (item) => !isNil(item[this.formFieldPath.initialCount]),
    );
  }

  public updateSavedInventoryItems(
    updateItem: IQohInventoryItemVenue | GridRowData,
  ): void {
    this.allInventoryItems = map(this.allInventoryItems, (item) => {
      if (isEqual(item.entity_id, updateItem.entity_id)) {
        return { ...item, ...updateItem };
      }

      return item;
    });
  }

  public async openViewSettingModal(): Promise<void> {
    const modalOptions = transferModalDefinition.viewSettingModal(
      this.viewSettingToggleValue,
    );
    const modalResult = await this.uiActions.promptFormModal(modalOptions);
    if (!modalResult.confirmed) return;
    this.updateGridByViewSetting(modalResult);
  }

  public updateGridByViewSetting(modalResult: IModalDialogResult): void {
    this.viewSettingToggleValue = get(modalResult, 'value.hide_empty_rows');
    const currentItems = getFormFieldValue(
      this.form,
      this.formFieldPath.inventoryItemVenues,
    );
    if (this.viewSettingToggleValue) {
      let filteredItems;
      let flattenItems = currentItems;
      if (this.isListView()) {
        filteredItems = this.filterBlankRows(flattenItems);
      } else {
        flattenItems = flatTreeDataByHasChildren(currentItems);
        filteredItems = this.filterBlankRows(flattenItems);
      }
      this.updateInventoryItemVenues(filteredItems as IQohInventoryItemVenue[]);
    } else {
      let mergedItems = cloneDeep(this.allInventoryItems);
      if (this.isListView()) {
        mergedItems = sortBy(mergedItems, (item) => toLower(item?.name));
      }
      this.updateInventoryItemVenues(mergedItems);
    }
  }

  public updateItemsByCompanyPreferences(
    data,
    path: string,
    markField: boolean = true,
    isChargeableTop: boolean = true,
  ): void {
    // When the user does not configure a list view or configures it as CATEGORY_VIEW
    // the list will be processed directly as a Category view
    if (
      isEqual(this.gridListView, GRID_LIST_VIEW_TYPE.ALPHABETICAL_LIST_VIEW)
    ) {
      const flattenTreeData = flatTreeDataByHasChildren<ITreeData>(data);
      const updateItems: ITreeData[] = isChargeableTop
        ? orderBy(
            flattenTreeData,
            [
              'is_chargeable',
              (item): string => toLower(item?.name || item?.item_name),
            ],
            ['desc', 'asc'],
          )
        : sortBy(flattenTreeData, (item) =>
            toLower(item?.name || item?.item_name),
          );
      setFormFieldValue(this.form, path, updateItems, markField);
    } else {
      setFormFieldValue(this.form, path, data, markField);
    }
  }

  public buildChangedItemWithRemote(
    reallyChangedItem: Record<string, Primitive | object>,
    editedItem: IQohInventoryItemVenue | GridRowData,
    remoteItem: IQohInventoryItemVenue,
  ): Record<string, Primitive | object> | null {
    if (isEmpty(reallyChangedItem)) {
      return null;
    }
    const itemEntityId = editedItem.entity_id?.toString();

    return {
      // need this for whole inventory item data pass to remote
      ...pick(editedItem, [
        this.formFieldPath.initialCount,
        this.formFieldPath.count,
        this.formFieldPath.measureEntityId,
      ]),
      ...reallyChangedItem,
      entity_id: itemEntityId,
      item_entity_id: itemEntityId,
      // we need get remote item _id here to keep items right
      _id: get(remoteItem, '_id'),
    };
  }

  private setQohFromVisible(action: XuxGridAction): void {
    const columnApi: ColumnApi =
      action.payload?.gridApi['columnController'].columnApi;
    const gridApi: GridApi = action.payload?.gridApi;

    columnApi?.setColumnsVisible(['qoh_from'], this.isQohFromVisible);

    if (isFunction(gridApi.sizeColumnsToFit)) {
      gridApi.sizeColumnsToFit();
    }
  }

  public async openClearQtyModal(
    text: string,
    fieldPath: string[],
  ): Promise<void> {
    const modalOptions = {
      text: this.i18nService.instant(
        I18n.Forms.TransferForm.AreYouSureYouWantToClearAllType,
        {
          type: this.i18nService.translate(text),
        },
      ),
    };
    const modalResult = await this.uiActions.confirm(modalOptions);
    if (!modalResult.confirmed) return;
    const currentItems = cloneDeep(
      getFormFieldValue(this.form, this.formFieldPath.inventoryItemVenues),
    );
    const isListView = this.isListView();
    let result = this.transferQtyService.clearField(
      fieldPath,
      isListView ? currentItems : flatTreeDataByHasChildren(currentItems),
    );

    result = this.afterClearQtyConfirmed(result);

    setFormFieldValue(
      this.form,
      this.formFieldPath.inventoryItemVenues,
      isListView ? result : this.convertInventoryItemVenuesToTree(result),
      true,
    );
    forEach(result, (item: IQohInventoryItemVenue) =>
      this.updateSavedInventoryItems(item),
    );
    if (!isListView) {
      setTimeout(() => {
        this.listGridApi.expandAll();
      });
    }
  }

  public afterClearQtyConfirmed(
    inventoryItems: IQohInventoryItemVenue[],
  ): IQohInventoryItemVenue[] {
    return inventoryItems;
  }

  public fetchItemsUPCCodes(
    inventoryItemIds: string[],
    inventoryItemVenues: ITransferItem[],
  ): Observable<ITransferItem[]> {
    return this.inventoryItemVenueService
      .fetchList(
        {
          filter: gt(size(inventoryItemIds), MAX_URL_QUERY_NUMBER)
            ? 'is_active eq true'
            : `entity_id eq in(${inventoryItemIds.join()})`,
          select: ['upc_codes'],
        },
        { fetchFullListData: true },
      )
      .pipe(
        rx.map((res: IApiListResponse) => {
          const entityToUpcMap = reduce(
            get(res, 'items'),
            (entityIdToUpcMap, item) => {
              entityIdToUpcMap[item.entity_id] = get(item, 'upc_codes', []);

              return entityIdToUpcMap;
            },
            {},
          );

          forEach(inventoryItemVenues, (itemVenue: ITransferItem) => {
            // provide below property for scan service.
            itemVenue.upc_codes =
              entityToUpcMap[itemVenue.item_entity_id] ?? itemVenue.upc_codes;
          });

          return inventoryItemVenues;
        }),
      );
  }

  public showTransferUnitTooltips(
    gridApi: GridApi,
    formFieldPath: string,
  ): void {
    setColumnDef(gridApi, formFieldPath, {
      headerComponentParams: {
        hintOptions: {
          description: this.i18nService.translate(
            I18n.Forms.TransferForm.TransferUnitTooltip,
          ),
          placement: 'top',
        },
      },
    });
  }
  // eslint-disable-next-line max-lines
}
